import { useEffect, useMemo, useRef, useState } from 'react';
import { RepresentativesTab } from '@/screens/parts/RepresentativesTab';
import type { FarmerRow } from '@/types/records';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FileJson, FileText, Layers, LayoutList, Map as MapIcon, Plus, Table2 } from 'lucide-react';
import { FarmerPhoto, usePhotoUrls } from '@/components/FarmerPhoto';
import { downloadPlotsGeoJson } from '@/lib/geojsonExport';
import { formatArea, formatSqm } from '@/lib/area';
import { placeLine } from '@/lib/address';
import {
  AddButton, Field, LiveScreen, QueryState, Select, StatusPill,
  formatDate, formatNumber, todayIso, useDebounced, useFormErrors, type Column,
} from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows, useSaveRow } from '@/hooks/useResource';
import { callRpc, createRow, removeRow, saveRow } from '@/api/resources';
import { EXCISION_REASONS, PLOT_SCOPES, type ScopeColumn } from '@/lib/plotScopes';
import { PlotScopeBulk } from '@/screens/parts/PlotScopeBulk';
import { NameVerificationPanel, type NameVerificationRow } from '@/components/NameVerificationPanel';
import { areaUnits, convertArea, farmerOptions, placeLabelOf, referenceValues, type AreaUnit } from '@/api/lookups';
import { useAuth } from '@/auth/AuthProvider';
import { can, isAdmin } from '@/auth/session';
import { StatusChanger } from '@/components/StatusChanger';
import { DeleteRecordButton } from '@/components/DeleteRecordButton';
import { statusStats } from '@/lib/recordStatus';
import { AreaUnitAdd } from '@/screens/parts/AreaUnitAdd';
import { RecordDrawer } from '@/components/RecordDrawer';
import { PlotMonitoring } from '@/screens/parts/PlotMonitoring';
import { Satellite, BadgeCheck, Users } from 'lucide-react';
import { usePlotAssets } from '@/screens/parts/PlotAssetsPanel';
import { PlotLandDocuments, saveLandDocument, type PendingLandDocument } from '@/screens/parts/PlotLandDocuments';
import { CONSERVATION_ASPECTS, FSC_CLAIMS, HCV_CATEGORIES, PLOT_TYPES, optionLabel } from '@/lib/practiceLists';
import { toast } from '@/lib/toast';
import { isSupabaseConfigured } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';
import type { PlotRow } from '@/types/records';
import { PlotMap, type PlotGeometry, type PolygonGeoJson } from '@/screens/parts/PlotMap';
import { GeographyPicker } from '@/screens/parts/GeographyPicker';
import { PlotConsentList } from '@/screens/parts/PlotConsent';
import { PlotChecklist } from '@/screens/parts/PlotChecklist';
import { RightsHolders, rightsModeOf } from '@/screens/parts/RightsHolders';
import { BoundaryStatus } from '@/screens/parts/BoundaryApproval';

interface FormState {
  farmer_id: string;
  plot_name: string;
  village_id: string;
  declared_area: string;
  declared_area_unit_id: string;
  land_tenure_type_id: string;
  lease_from: string;
  lease_to: string;
  lease_agreement_no: string;
  plot_type: string;
  hcv_categories: string[];
  conservation_aspects: string[];
  conservation_area_ha: string;
  conservation_notes: string;
  has_customary_rights: '' | 'yes' | 'no';
  customary_rights_detail: string;
  cb_approved_on: string;
  cb_approval_ref: string;
  fsc_claim: string;
  /** The programmes the plot is in (migration 0161), and each one's particulars by column. */
  scopes: string[];
  scope_values: Record<string, Partial<Record<ScopeColumn, string>>>;
}

interface PlotScopeRow {
  id: string;
  plot_id: string;
  scope_code: string;
  approved_on: string | null;
  reference_no: string | null;
  certificate_no: string | null;
  valid_to: string | null;
  assessment: string | null;
  project_name: string | null;
  notes: string | null;
  excision_declaration: string | null;
  excised_area_ha: number | null;
  excision_reason: string | null;
  excision_description: string | null;
}
/** The excision declaration in words, for the plot's view: "Excision: none declared", or the area and why. */
function excisionText(row?: PlotScopeRow): string | null {
  if (!row?.excision_declaration) return row ? 'Excision areas: not declared yet' : null;
  if (row.excision_declaration === 'none') return 'Excision areas: none';
  const reason = EXCISION_REASONS.find((entry) => entry.id === row.excision_reason)?.label ?? row.excision_reason ?? '';
  return `Excised: ${row.excised_area_ha ?? '?'} ha (${reason})${row.excision_description ? ` - ${row.excision_description}` : ''}`;
}
const SCOPE_COLUMNS: ScopeColumn[] = ['approved_on', 'reference_no', 'certificate_no', 'valid_to', 'assessment', 'project_name',
  'excision_declaration', 'excised_area_ha', 'excision_reason', 'excision_description'];

const EMPTY: FormState = {
  farmer_id: '', plot_name: '', village_id: '', declared_area: '',
  declared_area_unit_id: '', land_tenure_type_id: '', lease_from: '', lease_to: '', lease_agreement_no: '',
  plot_type: 'production', hcv_categories: [], conservation_aspects: [], conservation_area_ha: '', conservation_notes: '',
  has_customary_rights: '', customary_rights_detail: '', cb_approved_on: '', cb_approval_ref: '', fsc_claim: 'none',
  scopes: [], scope_values: {},
};

/** Hectares in one of each unit that is the same size everywhere; the rest depend on the state. */
const FIXED_HA: Record<string, number> = { HA: 1, ACRE: 0.404686, GUNTA: 0.010117, CENT: 0.004047, SQM: 0.0001, SQFT: 0.0000092903 };

/**
 * Plots and their boundaries.
 *
 * A plot is created first and mapped second: a field officer records what the farmer declares,
 * then walks the boundary, and those are often different visits. The declared area and the
 * mapped area are both kept, and the variance between them is derived by the database - it is
 * the number an auditor asks about, so neither side may be overwritten by the other.
 */
/** The plot form in tabs (owner, 2026-09-27: "make as seperate tab?", "draw boundary make as tab?",
 *  "add option to add land record docs?"). Plot, land records and conservation are one tab, "Plot
 *  details" ("keep in plot details"). */
type PlotTab = 'plot' | 'land' | 'names' | 'certification' | 'boundary' | 'rights' | 'consents';
const PLOT_TABS: Array<{ id: PlotTab; label: string }> = [
  { id: 'plot', label: 'Plot details' },
  { id: 'land', label: 'Land documents' },
  // Shown only once a land document names its holder differently from the ID card (owner,
  // 2026-09-27: "it checks and opens? activates the name clarifications?").
  { id: 'names', label: 'Name check' },
  { id: 'boundary', label: 'Boundary' },
  { id: 'certification', label: 'Scope' },
  // The family members, or the landowner and sharecropper - only for a tenure that has them, once
  // the plot is saved (owner, 2026-09-28: "tabbed separately and displayed on selection").
  { id: 'rights', label: 'Family / sharecropping' },
  // Consent is per plot (owner, 2026-09-27: "in plot, we need to have option to send consent and
  // consent history with validity"). Only on a saved plot: there is nothing to consent to before.
  { id: 'consents', label: 'Consents' },
];
/** Which tab each validated field lives on, so a failed save can take the officer to it. */
const PLOT_TAB_OF: Record<string, PlotTab> = {
  land_document_unsaved: 'land',
  farmer_id: 'plot', village_id: 'plot', declared_area: 'land', declared_area_unit_id: 'land', lease_to: 'land',
  hcv_categories: 'plot', conservation_aspects: 'plot', conservation_area_ha: 'plot', customary_rights_detail: 'plot',
  cb_approved_on: 'certification',
};
/** A scope's own fields are keyed `scope:<CODE>:<column>` and all live on the Scope tab. */
function plotTabOf(key: string): PlotTab | undefined {
  return key.startsWith('scope:') ? 'certification' : PLOT_TAB_OF[key];
}

export default function PlotScreen() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  // The header search lands here as ?q=<code>, so the register opens filtered to that record.
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  useEffect(() => { const q = params.get('q'); if (q !== null) setSearch(q); }, [params]);
  const debounced = useDebounced(search);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [editing, setEditing] = useState<PlotRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [mapping, setMapping] = useState<PlotRow | null>(null);
  const [formTab, setFormTab] = useState<PlotTab>('plot');
  // Bulk scope change on the plots ticked in the grid (owner, 2026-09-27).
  const [selectedPlots, setSelectedPlots] = useState<string[]>([]);
  const [bulkScope, setBulkScope] = useState(false);
  // The grid owns its ticks (Select rows in the header turns them on and off); after a bulk change
  // it is remounted so the applied selection does not linger.
  const [gridKey, setGridKey] = useState(0);
  // A new plot's boundary, drawn before the plot exists and saved with it.
  const [draftBoundary, setDraftBoundary] = useState<PolygonGeoJson | null>(null);
  // Land documents added before the plot exists, saved with it.
  const [pendingDocs, setPendingDocs] = useState<PendingLandDocument[]>([]);
  // A land document typed in but not saved: the plot's save stops rather than dropping it.
  const [landDocDirty, setLandDocDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stat, setStat] = useState<string | null>(null);
  const [addingUnit, setAddingUnit] = useState(false);
  // Table by default (owner, 2026-09-21: "instead of detail tile cards, table format"); the card
  // list stays one click away. Remembered per browser under a new key, so the earlier list
  // default does not stick; storage can fail.
  const [view, setView] = useState<'list' | 'table'>(() => {
    try { return window.localStorage.getItem('vanam.plots.view.v2') === 'list' ? 'list' : 'table'; } catch { return 'table'; }
  });
  function chooseView(next: 'list' | 'table') {
    setView(next);
    try { window.localStorage.setItem('vanam.plots.view.v2', next); } catch { /* optional */ }
  }
  const { errors, check, clear } = useFormErrors();
  const debouncedArea = useDebounced(form.declared_area);

  const plots = useRows<PlotRow>('farm_plots', {
    search: debounced ? { columns: ['plot_code', 'plot_name', 'farmer_name'], term: debounced } : undefined,
    orderBy: { column: 'created_at', ascending: false },
  });

  // ?open=<plot code> opens that plot straight away, map and all - the Risk assessment's "Open the
  // plot's map" lands here rather than on a list to search again. Used once, then dropped.
  useEffect(() => {
    const code = params.get('open');
    if (!code || !plots.data) return;
    const row = plots.data.find((plot) => plot.plot_code === code);
    if (row) setMapping(row);
    const next = new URLSearchParams(params);
    next.delete('open');
    setParams(next, { replace: true });
  }, [params, plots.data, setParams]);

  const farmers = useQuery({
    queryKey: ['picker', 'farmers'],
    queryFn: () => farmerOptions(),
    enabled: isSupabaseConfigured && (creating || Boolean(editing)),
  });
  const units = useQuery({
    queryKey: ['ref', 'unit', 'area'],
    queryFn: areaUnits,
    enabled: isSupabaseConfigured && (creating || Boolean(editing) || Boolean(mapping)),
  });
  const chosenUnit = (units.data ?? []).find((unit) => unit.id === form.declared_area_unit_id);

  // What will be stored as hectares, previewed with the factor the server will use for THIS
  // village - a Bigha in Nadia and a Bigha in Sitapur are different areas.
  const areaValue = Number(debouncedArea);
  const hectares = useQuery({
    queryKey: ['convert-area', areaValue, form.declared_area_unit_id, form.village_id],
    queryFn: () => convertArea(areaValue, form.declared_area_unit_id, form.village_id),
    enabled: isSupabaseConfigured && areaValue > 0 && Boolean(form.declared_area_unit_id) && Boolean(form.village_id),
  });
  const tenureTypes = useQuery({
    queryKey: ['ref', 'land_tenure_type'],
    queryFn: () => referenceValues('land_tenure_type'),
    enabled: isSupabaseConfigured && (creating || Boolean(editing) || Boolean(mapping)),
  });
  const isLeased = (tenureTypes.data ?? []).find((row) => row.id === form.land_tenure_type_id)?.value_code === 'LEASED';
  const tenureCode = (id: string | null | undefined) => (tenureTypes.data ?? []).find((row) => row.id === id)?.value_code ?? null;
  const formRights = rightsModeOf(tenureCode(form.land_tenure_type_id));

  // "Bigha (= 0.1338 ha)" in the unit list (owner, 2026-09-21). A fixed unit is the same
  // everywhere; a local unit's factor is the one set for this village's state, so it appears once
  // the village is chosen.
  const unitFactors = useQuery({
    queryKey: ['unit-factors', form.village_id, (units.data ?? []).length],
    queryFn: async () => Object.fromEntries(await Promise.all((units.data ?? []).filter((unit) => !unit.is_fixed).map(
      async (unit) => [unit.id, (await convertArea(1, unit.id, form.village_id))?.factor ?? null] as const))),
    enabled: isSupabaseConfigured && Boolean(form.village_id) && (units.data ?? []).length > 0,
  });
  const unitLabel = (unit: AreaUnit) => {
    const factor = unit.is_fixed ? FIXED_HA[unit.value_code] : unitFactors.data?.[unit.id];
    if (unit.value_code === 'HA') return unit.label;
    if (factor == null) return unit.is_fixed || !form.village_id ? unit.label : `${unit.label} (no factor here)`;
    return `${unit.label} (= ${formatNumber(factor, factor < 0.001 ? 7 : 4)} ha)`;
  };

  // The current boundary of the plot being mapped, as GeoJSON.
  // fn_plot_geojson returns a single GeoJSON Feature, not a FeatureCollection, and its
  // geometry is a MultiPolygon even when the plot was drawn as one ring.
  // The plot whose boundary is on screen: the one open in the view, or the one being edited when
  // its Boundary tab is showing.
  const boundaryPlot = mapping ?? (editing && formTab === 'boundary' ? editing : null);
  const geometry = useQuery({
    queryKey: ['plot-geometry', boundaryPlot?.id],
    queryFn: () => callRpc<{ type: string; geometry?: PlotGeometry } | null>(
      'fn_plot_geojson', { p_plot_id: boundaryPlot!.id }),
    enabled: isSupabaseConfigured && Boolean(boundaryPlot?.id),
  });

  // Where a new plot's map opens: the chosen village, not the whole of India.
  const newPlotPlace = useQuery({
    queryKey: ['place-of', form.village_id],
    queryFn: () => placeLabelOf(form.village_id),
    enabled: isSupabaseConfigured && creating && Boolean(form.village_id),
  });
  const savePlot = useSaveRow<PlotRow>('farm_plots');
  // The scopes of the plot being edited, loaded into the form once they arrive.
  const scopeRows = useRows<PlotScopeRow>('plot_scopes', { filters: { plot_id: editing?.id } }, Boolean(editing));
  const scopesLoadedFor = useRef<string | null>(null);
  // Name verifications raised by this plot's land documents.
  // The open plot's scopes, for its view (the form reads its own, above).
  const viewScopes = useRows<PlotScopeRow>('plot_scopes', { filters: { plot_id: mapping?.id } }, Boolean(mapping));
  const nameChecks = useRows<NameVerificationRow>('name_verifications', {
    filters: { plot_code: editing?.plot_code }, orderBy: { column: 'created_at', ascending: false },
  }, Boolean(editing?.plot_code));
  const openNameChecks = (nameChecks.data ?? []).filter((row) => row.verification_status === 'open').length;
  useEffect(() => {
    if (!editing || !scopeRows.data || scopesLoadedFor.current === editing.id) return;
    if (scopeRows.data.some((row) => row.plot_id !== editing.id)) return;
    scopesLoadedFor.current = editing.id;
    if (scopeRows.data.length === 0) return;
    setForm((now) => ({
      ...now,
      scopes: [...new Set([...now.scopes, ...scopeRows.data.map((row) => row.scope_code)])],
      scope_values: Object.fromEntries(scopeRows.data.map((row) => [row.scope_code,
        Object.fromEntries(SCOPE_COLUMNS.map((column) => [column, row[column] == null ? '' : String(row[column])]))])),
    }));
  }, [scopeRows.data, editing]);
  const mayCreate = can(profile, 'plot.register', 'create');
  const mayMap = can(profile, 'plot.mapping', 'create');
  const mayEditPlot = can(profile, 'plot.register', 'update');
  const mayViewMap = can(profile, 'plot.mapping', 'read');
  // A boundary file is an export like any other, so it follows the export right.
  const mayExportMap = can(profile, 'plot.mapping', 'export');

  async function exportGeoJson(rows: PlotRow[], fileName: string) {
    const mapped = rows.filter((row) => row.is_mapped);
    if (mapped.length === 0) { toast.error('None of these plots has a boundary yet.'); return; }
    try {
      const count = await downloadPlotsGeoJson(mapped.map((row) => row.id), fileName);
      if (count === 0) toast.error('No boundary could be exported.');
      else toast.success(`Exported ${count} feature${count === 1 ? '' : 's'} as GeoJSON.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The GeoJSON could not be exported.');
    }
  }

  /**
   * A plot that is not mapped cannot be claimed against EUDR, and one whose mapped area is far
   * from what the farmer declared is the thing an auditor opens first. Both get a card.
   */
  const STATS = useMemo(() => [
    { key: 'all', label: 'Plots', match: () => true, tone: 'neutral' as const },
    { key: 'draft', label: 'Drafts', tone: 'info' as const, match: (row: PlotRow) => Boolean(row.is_draft) },
    { key: 'unmapped', label: 'Not mapped', tone: 'error' as const,
      match: (row: PlotRow) => !row.is_mapped },
    { key: 'variance', label: 'Area variance > 10%', tone: 'warn' as const,
      match: (row: PlotRow) => Math.abs(Number(row.area_variance_pct ?? 0)) > 10 },
    ...statusStats<PlotRow>((row) => row.plot_status),
  ], []);

  const visible = useMemo(() => applyStat(plots.data ?? [], STATS, stat), [plots.data, STATS, stat]);
  const photos = usePhotoUrls((plots.data ?? []).map((row) => row.farmer_photo_document_id));

  const columns: Array<Column<PlotRow>> = useMemo(() => [
    { key: 'farmer_photo', label: '', render: (row) => (
      <FarmerPhoto name={row.farmer_name} size={32}
                   url={row.farmer_photo_document_id ? photos.data?.[row.farmer_photo_document_id] : null} />
    ) },
    { key: 'plot_code', label: 'Plot' },
    { key: 'plot_no', label: 'Plot no.', render: (row) => (row.plot_no
      ? <span title={`Plot ${row.plot_no.slice(1)} of ${row.farmer_plot_count ?? '?'} held by this farmer`}>
          <strong>{row.plot_no}</strong><span className="muted"> / {row.farmer_plot_count ?? '?'}</span>
        </span>
      : '--') },
    { key: 'plot_name', label: 'Plot name',
      render: (row) => <>{row.plot_name ?? '--'}{row.is_draft && <span className="draft-badge">Draft</span>}</> },
    { key: 'farmer_name', label: 'Farmer', render: (row) => row.farmer_name ?? '--' },
    { key: 'village_name', label: 'Village', render: (row) => row.village_name ?? '--' },
    { key: 'declared_area_ha', label: 'Declared', align: 'right', render: (row) => <AreaCell ha={row.declared_area_ha} /> },
    { key: 'mapped_area_ha', label: 'Mapped', align: 'right', render: (row) => <AreaCell ha={row.mapped_area_ha} /> },
    {
      key: 'area_variance_pct',
      label: 'Variance',
      align: 'right',
      render: (row) => (row.area_variance_pct === null || row.area_variance_pct === undefined
        ? '--'
        : `${formatNumber(row.area_variance_pct, 1)}%`),
    },
    { key: 'total_tree_count', label: 'Trees', align: 'right', render: (row) => formatNumber(row.total_tree_count) },
    { key: 'plot_status', label: 'Status', render: (row) => <StatusPill value={row.plot_status} /> },
    {
      key: 'map',
      label: '',
      render: (row) => (
        <button
          onClick={(event) => { event.stopPropagation(); setMapping(row); }}
          title={row.is_mapped ? 'View or redraw the boundary' : 'Draw the boundary'}
          type="button"
        >
          <MapIcon size={14} /> {row.is_mapped ? 'Boundary' : 'Map it'}
        </button>
      ),
    },
  ], [photos.data]);

  function openCreate() {
    clear();
    setFormTab('plot');
    setDraftBoundary(null);
    setPendingDocs([]);
    setLandDocDirty(false);
    setForm(EMPTY);
    setCreating(true);
  }

  function openEdit(row: PlotRow) {
    clear();
    setLandDocDirty(false);
    scopesLoadedFor.current = null;
    setFormTab('plot');
    setForm({
      farmer_id: row.farmer_id,
      plot_name: row.plot_name ?? '',
      village_id: row.village_id,
      declared_area: row.declared_area?.toString() ?? '',
      declared_area_unit_id: row.declared_area_unit_id ?? '',
      land_tenure_type_id: row.land_tenure_type_id ?? '',
      lease_from: row.lease_from ?? '',
      lease_to: row.lease_to ?? '',
      lease_agreement_no: row.lease_agreement_no ?? '',
      plot_type: row.plot_type ?? 'production',
      hcv_categories: row.hcv_categories ?? [],
      conservation_aspects: row.conservation_aspects ?? [],
      conservation_area_ha: row.conservation_area_ha?.toString() ?? '',
      conservation_notes: row.conservation_notes ?? '',
      has_customary_rights: row.has_customary_rights == null ? '' : row.has_customary_rights ? 'yes' : 'no',
      customary_rights_detail: row.customary_rights_detail ?? '',
      cb_approved_on: row.cb_approved_on ?? '',
      cb_approval_ref: row.cb_approval_ref ?? '',
      fsc_claim: row.fsc_claim ?? 'none',
      // Filled in by the effect below once the plot's scope rows arrive. A plot recorded before
      // scopes existed, with an FSC claim or a CB approval, is in FSC FM.
      scopes: row.fsc_claim && row.fsc_claim !== 'none' || row.cb_approved_on ? ['FSC_FM'] : [],
      scope_values: {},
    });
    setEditing(row);
  }

  /** Each selected scope's required particulars. */
  function scopeProblems(): Record<string, string | undefined> {
    const problems: Record<string, string | undefined> = {};
    for (const code of form.scopes) {
      const scope = PLOT_SCOPES.find((entry) => entry.code === code);
      for (const field of scope?.fields ?? []) {
        if (field.showIf && !field.showIf(form.scope_values[code] ?? {})) continue;
        const value = form.scope_values[code]?.[field.column]?.trim();
        if (field.required && !value) problems[`scope:${code}:${field.column}`] = `${scope?.label}: ${field.label} is needed.`;
        if (field.kind === 'number' && value && !(Number(value) > 0)) problems[`scope:${code}:${field.column}`] = `${scope?.label}: ${field.label} must be more than 0.`;
        if (field.kind === 'date' && field.column === 'approved_on' && value && value > todayIso()) {
          problems[`scope:${code}:${field.column}`] = `${scope?.label}: ${field.label} cannot be in the future.`;
        }
      }
      const from = form.scope_values[code]?.approved_on;
      const to = form.scope_values[code]?.valid_to;
      if (from && to && to < from) problems[`scope:${code}:valid_to`] = `${scope?.label}: it must end after it starts.`;
    }
    return problems;
  }

  /** One row per selected scope; a scope taken off the plot is removed with a reason. */
  async function syncScopes(plotId: string) {
    const existing = editing ? scopeRows.data ?? [] : [];
    for (const code of form.scopes) {
      const definition = PLOT_SCOPES.find((entry) => entry.code === code);
      // A column this scope does not ask about, or asks about only in another case, is saved empty.
      const asked = (column: ScopeColumn) => {
        const field = definition?.fields.find((entry) => entry.column === column);
        return !field?.showIf || field.showIf(form.scope_values[code] ?? {});
      };
      const values = Object.fromEntries(SCOPE_COLUMNS.map((column) => [column, asked(column) ? form.scope_values[code]?.[column]?.trim() || null : null]));
      const row = existing.find((entry) => entry.scope_code === code);
      if (row) await saveRow('plot_scopes', row.id, values);
      else await createRow('plot_scopes', { plot_id: plotId, scope_code: code, ...values });
    }
    for (const row of existing.filter((entry) => !form.scopes.includes(entry.scope_code))) {
      await removeRow('plot_scopes', row.id, 'Scope taken off the plot.');
    }
    await queryClient.invalidateQueries({ queryKey: ['plot_scopes'] });
  }

  async function handleSave(asDraft = false) {
    const area = form.declared_area.trim();

    // A draft asks for what the database needs to create the row and nothing else: the farmer it
    // hangs off and the village its plot code is issued from. Everything the officer DID fill in
    // is still checked - a draft may be unfinished, but it may not be wrong, or the errors simply
    // wait to be discovered by whoever completes it.
    const fscFm = form.scopes.includes('FSC_FM');
    const always = {
      land_document_unsaved: landDocDirty
        ? 'A land document is filled in but not saved. Press Save land document, or Clear it.' : undefined,
      farmer_id: form.farmer_id ? undefined : 'Choose the farmer this plot belongs to.',
      village_id: form.village_id ? undefined : 'Choose the village.',
      declared_area: !area || Number(area) > 0 ? undefined : 'A declared area must be greater than zero.',
      lease_to: isLeased && form.lease_from && form.lease_to && form.lease_to < form.lease_from
        ? 'The lease cannot end before it starts.' : undefined,
      conservation_area_ha: form.conservation_area_ha && !(Number(form.conservation_area_ha) > 0) ? 'Give the area in hectares, more than 0.' : undefined,
      cb_approved_on: form.cb_approved_on && form.cb_approved_on > todayIso()
        ? 'The approval date cannot be in the future.' : undefined,
    };
    const whenComplete = {
      declared_area_unit_id: !area || form.declared_area_unit_id ? undefined : 'Which unit is that area in?',
      hcv_categories: form.plot_type === 'hcv_area' && form.hcv_categories.length === 0 ? 'Choose the HCV categories this area holds.' : undefined,
      conservation_aspects: ['conservation_area', 'mixed'].includes(form.plot_type) && form.conservation_aspects.length === 0
        ? 'Choose what is being conserved.' : undefined,
      customary_rights_detail: form.has_customary_rights === 'yes' && !form.customary_rights_detail.trim()
        ? 'Describe the customary rights: who holds them, and what they allow.' : undefined,
      ...scopeProblems(),
      cb_approved_on: fscFm && form.fsc_claim === 'fsc_100' && !form.cb_approved_on
        ? 'An FSC 100% claim needs the date the certification body approved the plot as a member.'
        : always.cb_approved_on,
    };
    const problems: Record<string, string | undefined> = asDraft ? always : { ...always, ...whenComplete };
    if (!check(problems)) {
      const first = Object.keys(problems).find((key) => problems[key]);
      if (first) setFormTab(plotTabOf(first) ?? 'plot');
      return;
    }

    setSaving(true);
    try {
      const saved = await savePlot.mutateAsync({
        id: editing?.id,
        values: {
          farmer_id: form.farmer_id,
          plot_name: form.plot_name.trim() || null,
          village_id: form.village_id,
          declared_area: area ? Number(area) : null,
          declared_area_unit_id: form.declared_area_unit_id || null,
          land_tenure_type_id: form.land_tenure_type_id || null,
          lease_from: isLeased ? form.lease_from || null : null,
          lease_to: isLeased ? form.lease_to || null : null,
          lease_agreement_no: isLeased ? form.lease_agreement_no.trim() || null : null,
          plot_type: form.plot_type,
          hcv_categories: form.plot_type === 'hcv_area' || form.plot_type === 'mixed' ? form.hcv_categories : [],
          conservation_aspects: form.plot_type === 'production' ? [] : form.conservation_aspects,
          conservation_area_ha: form.plot_type === 'production' || !form.conservation_area_ha ? null : Number(form.conservation_area_ha),
          conservation_notes: form.plot_type === 'production' ? null : form.conservation_notes.trim() || null,
          has_customary_rights: form.has_customary_rights === '' ? null : form.has_customary_rights === 'yes',
          customary_rights_detail: form.has_customary_rights === 'yes' ? form.customary_rights_detail.trim() : null,
          // Out of FSC FM, the plot carries no FSC approval or claim.
          cb_approved_on: fscFm ? form.cb_approved_on || null : null,
          cb_approval_ref: fscFm ? form.cb_approval_ref.trim() || null : null,
          fsc_claim: fscFm ? form.fsc_claim : 'none',
          is_draft: asDraft,
        },
      });
      // A boundary drawn before the plot existed is saved now that it does. If that fails the plot
      // is still saved, and the officer is told to draw it again rather than losing the plot.
      if (!editing && draftBoundary && saved) {
        try {
          const result = await callRpc<{ area_ha: number; overlap_pct: number | null; overlaps_plot_id: string | null }>(
            'fn_save_plot_geometry', { p_plot_id: saved.id, p_geojson: draftBoundary, p_capture_method: 'draw' },
          );
          await queryClient.invalidateQueries({ queryKey: ['farm_plots'] });
          toast.success(`Plot saved with its boundary: ${formatArea(result.area_ha)}.`);
          if (result.overlaps_plot_id && (result.overlap_pct ?? 0) > 0) {
            toast.error(`This boundary overlaps another plot by ${formatNumber(result.overlap_pct, 1)}%. Check before submitting.`);
          }
        } catch (error) {
          toast.error(`Plot saved, but the boundary was not: ${error instanceof Error ? error.message : 'save failed'}. Open the plot and draw it again.`);
        }
      }
      if (saved) {
        try {
          await syncScopes(saved.id);
        } catch (error) {
          toast.error(`Plot saved, but its scopes were not: ${error instanceof Error ? error.message : 'save failed'}.`);
        }
      }
      if (!editing && saved && pendingDocs.length > 0 && profile?.organization) {
        const failed: string[] = [];
        for (const item of pendingDocs) {
          try {
            await saveLandDocument(saved.id, profile.organization.id, item);
          } catch {
            failed.push(item.typeLabel);
          }
        }
        await queryClient.invalidateQueries({ queryKey: ['plot_land_documents'] });
        if (failed.length > 0) toast.error(`Plot saved, but these land documents were not: ${failed.join(', ')}. Open the plot and add them again.`);
      }
      setCreating(false);
      setEditing(null);
      if (asDraft) toast.success('Saved as a draft. Complete it when you have the rest.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  /**
   * The boundary is not written through the api view: geometry needs PostGIS, and the area,
   * the "current" flag and the overlap check are all derived server-side.
   */
  async function saveBoundary(polygon: PolygonGeoJson | null) {
    if (!boundaryPlot || !polygon) return;
    try {
      const result = await callRpc<{ area_ha: number; overlap_pct: number | null; overlaps_plot_id: string | null }>(
        'fn_save_plot_geometry',
        { p_plot_id: boundaryPlot.id, p_geojson: polygon, p_capture_method: 'draw' },
      );
      await queryClient.invalidateQueries({ queryKey: ['farm_plots'] });
      await queryClient.invalidateQueries({ queryKey: ['plot-geometry', boundaryPlot.id] });
      await queryClient.invalidateQueries({ queryKey: ['plot-boundaries', boundaryPlot.id] });
      toast.success(`Boundary saved as a draft: ${formatArea(result.area_ha)}. Submit it for approval below; the satellite watches it once approved.`);
      if (result.overlaps_plot_id && (result.overlap_pct ?? 0) > 0) {
        // Not refused: overlapping claims are real and have to be recorded before they can be
        // resolved. The officer is told so it can be raised while they are still on site.
        toast.error(`This boundary overlaps another plot by ${formatNumber(result.overlap_pct, 1)}%. Check before submitting.`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The boundary could not be saved.');
    }
  }

  const currentPolygon = geometry.data?.geometry ?? null;

  // Read the open plot back out of the list rather than using the row captured when the
  // drawer opened: saving a boundary re-derives the mapped area and the variance, and a
  // snapshot would keep showing the figures from before the edit.
  const assets = usePlotAssets(boundaryPlot?.id);
  const liveEditing = editing ? (plots.data ?? []).find((row) => row.id === editing.id) ?? editing : null;
  const openPlot = mapping ? (plots.data ?? []).find((row) => row.id === mapping.id) ?? mapping : null;
  const openRights = openPlot ? rightsModeOf(tenureCode(openPlot.land_tenure_type_id)) : null;
  const openPlotLabel = openPlot ? `${openPlot.plot_code}${openPlot.plot_no ? ` · ${openPlot.plot_no}` : ''}` : undefined;

  return (
    <LiveScreen
      actions={<AddButton disabled={!mayCreate} label="New plot" onClick={openCreate}
                          title={mayCreate ? undefined : 'Your role cannot create plots.'} />}
      subtitle="Land parcels, their declared and mapped area, and the boundary walked on site."
      title="Plots"
    >
      <div className="toolbar-row">
        <div aria-label="How to show plots" className="view-toggle" role="group">
          <button aria-pressed={view === 'table'} onClick={() => chooseView('table')} title="Show as a table" type="button">
            <Table2 size={16} /> Table
          </button>
          <button aria-pressed={view === 'list'} onClick={() => chooseView('list')} title="Show as a list" type="button">
            <LayoutList size={16} /> List
          </button>
        </div>
        {/* GeoJSON is offered only for plots ticked in the table, and downloads only those (owner,
            2026-09-27: "geojson display can be active only for the selected rows ... rest of the
            time invisible"). Tick rows with Select rows in the header. */}
      </div>

      <StatCards active={stat} onSelect={setStat} rows={plots.data ?? []} secondary={{ sum: (row) => row.mapped_area_ha ?? row.declared_area_ha, unit: 'ha' }} stats={STATS} />

      <QueryState empty="No plots yet. Add one against a farmer." query={plots}>
        {view === 'table' ? (
          <DataGrid bulkActions={mayEditPlot || mayExportMap ? (ids) => (
                      <>
                        {mayExportMap && (
                          <button className="dg-btn" type="button"
                                  onClick={() => void exportGeoJson((plots.data ?? []).filter((row) => ids.includes(row.id)), `plots-${todayIso()}`)}
                                  title="Download the boundaries of the selected plots as one GeoJSON file">
                            <FileJson size={14} /> GeoJSON ({ids.length})
                          </button>
                        )}
                        {mayEditPlot && (
                          <button className="dg-btn" onClick={() => { setSelectedPlots(ids); setBulkScope(true); }} type="button">
                            <Layers size={14} /> Update scope ({ids.length})
                          </button>
                        )}
                      </>
                    ) : undefined}
                    columns={columns} exportName="plots" key={gridKey} onRowClick={setMapping}
                    onSearchChange={setSearch} rows={visible} screenKey="plot.register" search={search}
                    searchPlaceholder="Plot code, name or farmer" />
        ) : (
          <ul className="plot-list">
            {visible.map((row) => (
              <li key={row.id}>
                <button className="plot-card" onClick={() => setMapping(row)} type="button">
                  <FarmerPhoto name={row.farmer_name}
                               url={row.farmer_photo_document_id ? photos.data?.[row.farmer_photo_document_id] : null} />
                  <span className="plot-card-main">
                    <span className="plot-card-title">
                      <strong>{row.plot_no ?? '--'}</strong>
                      <span>{row.plot_name ?? row.plot_code}</span>
                    </span>
                    <span className="plot-card-farmer">{row.farmer_name ?? '--'} · {row.plot_code}</span>
                    <span className="muted">{placeLine(row)}</span>
                    <span className="plot-card-area">
                      Declared {formatArea(row.declared_area_ha)} · Mapped {formatArea(row.mapped_area_ha)}
                    </span>
                  </span>
                  <span className="plot-card-side">
                    <StatusPill value={row.plot_status} />
                  </span>
                </button>
                <span className="plot-card-actions">
                  <button onClick={() => setMapping(row)} type="button">
                    <MapIcon size={14} /> {row.is_mapped ? 'Boundary' : 'Map it'}
                  </button>
                  {/* No GeoJSON per card: it is offered only for the rows ticked in the table (owner,
                      2026-09-27: "geojson ... active only for the selected rows ... rest of the time invisible"). */}
                </span>
              </li>
            ))}
          </ul>
        )}
      </QueryState>

      <RecordDrawer
        footer={
          <>
            {editing && (
              <DeleteRecordButton entity="plot" id={editing.id} invalidate={['farm_plots', 'farmers']}
                                  label={`plot ${editing.plot_code}`} onDeleted={() => setEditing(null)} screenKey="plot.register" />
            )}
            <button className="cancel" onClick={() => { setCreating(false); setEditing(null); }} type="button">Cancel</button>
            {(!editing || editing.is_draft) && (
              <button disabled={saving} onClick={() => void handleSave(true)}
                      title="Save what you have; only the farmer and village are needed" type="button">
                <FileText size={14} /> Save draft
              </button>
            )}
            <button className="primary" disabled={saving} onClick={() => void handleSave(false)} type="button">
              {saving ? 'Saving...' : editing?.is_draft ? 'Complete & save' : editing ? 'Save changes' : 'Create plot'}
            </button>
          </>
        }
        onClose={() => { setCreating(false); setEditing(null); }}
        open={creating || Boolean(editing)}
        record={editing ? { table: 'ops.farm_plots', id: editing.id, historyIds: [editing.current_geometry_id], invalidate: ['farm_plots'] } : null}
        subtitle={editing ? `${editing.plot_code}${editing.plot_no ? ` · ${editing.plot_no} of ${editing.farmer_plot_count ?? '?'}` : ''}` : 'Numbered under the farmer when saved: WB_ML_1/P1, /P2, ...'}
        title={editing ? 'Edit plot' : 'New plot'}
        wide={formTab === 'boundary'}
        sections={{
          items: PLOT_TABS.filter((entry) => (entry.id !== 'names' || (nameChecks.data ?? []).length > 0) && (entry.id !== 'consents' || Boolean(editing))
                                   && (entry.id !== 'rights' || (Boolean(editing) && Boolean(formRights)))).map((entry) => {
            const count = entry.id === 'names' ? openNameChecks
              : Object.entries(errors).filter(([key, value]) => value && plotTabOf(key) === entry.id).length;
            const label = entry.id === 'rights' ? (formRights === 'family' ? 'Family members' : 'Sharecropping') : entry.label;
            return { id: entry.id, label: <>{label}{count > 0 && <span className="tab-error-count">{count}</span>}</> };
          }),
          active: formTab,
          onChange: (id) => setFormTab(id as PlotTab),
        }}
      >

        {formTab === 'plot' && (<>
        <Field error={errors.farmer_id} label="Farmer" required>
          <Select
            onChange={(value) => setForm({ ...form, farmer_id: value })}
            options={farmers.data ?? []}
            placeholder={farmers.isLoading ? 'Loading...' : 'Select farmer'}
            value={form.farmer_id}
          />
        </Field>
        <Field hint="What the farmer calls it, e.g. the north block." label="Plot name">
          <input onChange={(event) => setForm({ ...form, plot_name: event.target.value })} value={form.plot_name} />
        </Field>

        <GeographyPicker
          error={errors.village_id}
          onChange={(villageId) => setForm({ ...form, village_id: villageId })}
          value={form.village_id}
        />

        <div className="section-heading"><h3>Plot type and conservation</h3></div>
        <Field label="Farm plot type">
          <Select onChange={(value) => setForm({ ...form, plot_type: value || 'production' })} options={PLOT_TYPES} value={form.plot_type} />
        </Field>
        {(form.plot_type === 'hcv_area' || form.plot_type === 'mixed') && (
          <Field error={errors.hcv_categories} label="High Conservation Values present" required={form.plot_type === 'hcv_area'}>
            <ChipPicker onChange={(value) => setForm({ ...form, hcv_categories: value })} options={HCV_CATEGORIES} value={form.hcv_categories} />
          </Field>
        )}
        {form.plot_type !== 'production' && (
          <>
            <Field error={errors.conservation_aspects} label="Conservation aspects" required={form.plot_type !== 'hcv_area'}>
              <ChipPicker onChange={(value) => setForm({ ...form, conservation_aspects: value })} options={CONSERVATION_ASPECTS} value={form.conservation_aspects} />
            </Field>
            <div className="field-row">
              <Field error={errors.conservation_area_ha} hint="The part set aside, if not the whole plot." label="Conserved area (ha)">
                <input inputMode="decimal" onChange={(event) => setForm({ ...form, conservation_area_ha: event.target.value })} value={form.conservation_area_ha} />
              </Field>
              <Field label="Conservation notes">
                <input onChange={(event) => setForm({ ...form, conservation_notes: event.target.value })}
                       placeholder="e.g. 20 m buffer along the stream, no felling" value={form.conservation_notes} />
              </Field>
            </div>
          </>
        )}

        <Field label="Customary rights on this plot?">
          <div className="view-toggle member-type" role="group" aria-label="Customary rights">
            {([['yes', 'Yes'], ['no', 'No'], ['', 'Not assessed']] as const).map(([value, label]) => (
              <button aria-pressed={form.has_customary_rights === value} key={label} onClick={() => setForm({ ...form, has_customary_rights: value })} type="button">{label}</button>
            ))}
          </div>
        </Field>
        {form.has_customary_rights === 'yes' && (
          <Field error={errors.customary_rights_detail} hint="Who holds them and what they allow: grazing, a path, collecting firewood or leaves, a sacred grove ..."
                 label="Customary rights - description" required>
            <textarea onChange={(event) => setForm({ ...form, customary_rights_detail: event.target.value })} rows={3} value={form.customary_rights_detail} />
          </Field>
        )}

        {editing && (
          <StatusChanger current={liveEditing?.plot_status ?? editing.plot_status} entity="plot" id={editing.id}
                         invalidate={['farm_plots']} key={editing.id + (liveEditing?.plot_status ?? '')} screenKey="plot.mapping" />
        )}
        </>)}

        {/* Kept mounted and only hidden, so a land document half typed in survives a look at
            another tab and the unsaved-document check stays true to what is on screen. */}
        <div hidden={formTab !== 'land'} style={{ display: formTab === 'land' ? 'grid' : 'none', gap: 14 }}>
        {/* Tenure, lease and declared area sit inside the land documents they are read off. */}
        {(() => {
          const landFields = (<>
        <Field label="Land tenure">
          <Select onChange={(value) => setForm({ ...form, land_tenure_type_id: value })}
                  options={(tenureTypes.data ?? []).map((row) => ({ id: row.id, label: row.label }))}
                  value={form.land_tenure_type_id} />
        </Field>
        {isLeased && (
          <>
            <div className="field-row">
              <Field label="Lease from">
                <input onChange={(event) => setForm({ ...form, lease_from: event.target.value })} type="date" value={form.lease_from} />
              </Field>
              <Field error={errors.lease_to} label="Lease to">
                <input min={form.lease_from || undefined} onChange={(event) => setForm({ ...form, lease_to: event.target.value })}
                       type="date" value={form.lease_to} />
              </Field>
            </div>
            <Field hint="As written on the lease deed or agreement." label="Agreement number">
              <input onChange={(event) => setForm({ ...form, lease_agreement_no: event.target.value })} value={form.lease_agreement_no} />
            </Field>
          </>
        )}


        <div className="field-row">
          <Field error={errors.declared_area} hint="As written in the land record (patta, RoR, khatian ...). The mapped area is measured separately." label="Declared area (as per land record)">
            <input inputMode="decimal" onChange={(event) => setForm({ ...form, declared_area: event.target.value })}
                   value={form.declared_area} />
          </Field>
          <Field error={errors.declared_area_unit_id} label="Unit"
                 hint={chosenUnit && !chosenUnit.is_fixed ? 'Varies by state - converted with this village\'s state factor.' : undefined}>
            <div className="select-with-add">
              <Select onChange={(value) => setForm({ ...form, declared_area_unit_id: value })}
                      options={(units.data ?? []).map((row) => ({ id: row.id, label: unitLabel(row) }))}
                      placeholder={units.isLoading ? 'Loading...' : 'Select unit'}
                      value={form.declared_area_unit_id} />
              <button aria-label="Add a unit" disabled={!isAdmin(profile)} onClick={() => setAddingUnit(true)}
                      title={isAdmin(profile) ? 'Add a land unit and its hectare factor' : 'Only a Project Admin can add units.'}
                      type="button">
                <Plus size={16} />
              </button>
            </div>
          </Field>
        </div>
        {addingUnit && (
          <AreaUnitAdd
            onAdded={(unitId) => { setAddingUnit(false); setForm((current) => ({ ...current, declared_area_unit_id: unitId })); }}
            onCancel={() => setAddingUnit(false)}
          />
        )}
        {areaValue > 0 && form.declared_area_unit_id && (
          <p className="area-preview">
            {!form.village_id
              ? 'Choose the village to see this in hectares.'
              : hectares.isLoading
                ? 'Converting...'
                : hectares.data?.hectares != null
                  ? <>= <strong>{formatNumber(hectares.data.hectares, 3)} ha</strong>
                      {' '}(1 {chosenUnit?.label ?? 'unit'} = {formatNumber(hectares.data.factor, 3)} ha
                      {hectares.data.rule_status === 'draft' ? ', factor not yet confirmed' : ''})</>
                  : <span className="field-error">No hectare factor is set for this unit in this state. A Project Admin can add one; until then the plot reports no hectares.</span>}
          </p>
        )}
          </>);
          return editing
            ? <PlotLandDocuments farmerId={editing.farmer_id} onDirtyChange={setLandDocDirty} onNameCheckRaised={() => setFormTab('names')} plotId={editing.id}>{landFields}</PlotLandDocuments>
            : <PlotLandDocuments farmerId={form.farmer_id || null} onDirtyChange={setLandDocDirty} onPendingChange={setPendingDocs} pending={pendingDocs}>{landFields}</PlotLandDocuments>;
        })()}
        </div>

        {formTab === 'names' && (<>
        <p className="muted" style={{ margin: 0 }}>
          A land document names its holder differently from the farmer's ID card - "Md Sheik" against "Mohammed
          Sheik". Record what shows they are the same person; someone other than the maker accepts or rejects it.
        </p>
        {(nameChecks.data ?? []).map((row) => (
          <section className="name-check" key={row.id}>
            <div className="section-heading">
              <h3>{row.verification_code ?? 'Name verification'} <span className="muted">· {row.land_document_type ?? 'document'}{row.land_document_no ? ` ${row.land_document_no}` : ''}</span></h3>
            </div>
            <NameVerificationPanel row={row} />
          </section>
        ))}
        </>)}

        {formTab === 'rights' && editing && formRights && <RightsHolders mode={formRights} plotId={editing.id} />}

        {formTab === 'consents' && editing && (<>
          <PlotChecklist farmerCode={editing.farmer_code} linkOnly onPlotPage plotCode={editing.plot_code} plotId={editing.id} />
          <PlotConsentList linkOnly plotId={editing.id} />
        </>)}

        {formTab === 'certification' && (<>
        {/* Plain checkboxes, not pills (owner, 2026-09-27: "remove the circled boxes arround text, can
            we shift this to scope tab?"). Each ticked scope's own fields follow below it. */}
        <div className="section-heading"><h3>Scope</h3></div>
        <p className="muted" style={{ margin: 0 }}>Tick every programme this plot is in; each one's details appear below.</p>
        <div aria-label="Scopes" className="check-list is-stacked" role="group">
          {PLOT_SCOPES.map((scope) => (
            <label key={scope.code}>
              <input checked={form.scopes.includes(scope.code)} type="checkbox"
                     onChange={(event) => setForm({
                       ...form,
                       scopes: event.target.checked ? [...form.scopes, scope.code] : form.scopes.filter((code) => code !== scope.code),
                     })} />
              {scope.label}
            </label>
          ))}
        </div>
        {PLOT_SCOPES.filter((scope) => form.scopes.includes(scope.code)).map((scope) => (
          <div key={scope.code} style={{ display: 'grid', gap: 12 }}>
            <div className="section-heading"><h3>{scope.label}</h3></div>
            {scope.usesPlotFields ? (<>
              <div className="field-row">
                <Field error={errors.cb_approved_on} hint="When the certification body approved this plot as a group member." label="Date of approval by CB as member">
                  <input max={todayIso()} onChange={(event) => setForm({ ...form, cb_approved_on: event.target.value })} type="date" value={form.cb_approved_on} />
                </Field>
                <Field label="CB approval reference">
                  <input onChange={(event) => setForm({ ...form, cb_approval_ref: event.target.value })} placeholder="Audit report / member list ref." value={form.cb_approval_ref} />
                </Field>
              </div>
              <Field hint="FSC 100% needs the CB approval date. A harvest can claim no more than its plot." label="FSC claim">
                <Select onChange={(value) => setForm({ ...form, fsc_claim: value || 'none' })} options={FSC_CLAIMS} value={form.fsc_claim} />
              </Field>
            </>) : null}
            {scope.fields.length > 0 && (
              <div className="field-row">
                {scope.fields.filter((field) => !field.showIf || field.showIf(form.scope_values[scope.code] ?? {})).map((field) => {
                  const value = form.scope_values[scope.code]?.[field.column] ?? '';
                  const set = (next: string) => setForm({
                    ...form,
                    scope_values: { ...form.scope_values, [scope.code]: { ...form.scope_values[scope.code], [field.column]: next } },
                  });
                  return (
                    <Field error={errors[`scope:${scope.code}:${field.column}`]} hint={field.hint} key={field.column}
                           label={field.label} required={field.required}>
                      {field.kind === 'choice'
                        ? <Select onChange={set} options={field.choices ?? []} value={value} />
                        : field.kind === 'textarea'
                          ? <textarea onChange={(event) => set(event.target.value)} rows={2} value={value} />
                          : <input inputMode={field.kind === 'number' ? 'decimal' : undefined}
                                   max={field.kind === 'date' && field.column === 'approved_on' ? todayIso() : undefined}
                                   onChange={(event) => set(event.target.value)} type={field.kind === 'date' ? 'date' : 'text'} value={value} />}
                    </Field>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        </>)}

        {formTab === 'boundary' && (<>
        <div className="section-heading"><h3>Boundary</h3></div>
        {!editing ? (
          !mayMap ? (
            <p className="muted" style={{ margin: 0 }}>Your role cannot draw plot boundaries.</p>
          ) : (<>
            <p className="muted" style={{ margin: 0 }}>
              Draw the boundary now; it is saved with the plot when you press <strong>Create plot</strong> or
              <strong> Save draft</strong>, and the mapped area is worked out then. Assets and risks are marked once the
              plot is saved.
            </p>
            <PlotMap onChange={setDraftBoundary} placeHint={newPlotPlace.data} value={draftBoundary} />
          </>)
        ) : !liveEditing ? null : !mayViewMap ? (
          <p className="muted" style={{ margin: 0 }}>Your role cannot view plot boundaries.</p>
        ) : (<>
          <dl className="detail-grid">
            <div><dt>Declared (land record)</dt><dd>{formatArea(liveEditing.declared_area_ha)}</dd></div>
            <div><dt>Mapped</dt><dd>{formatArea(liveEditing.mapped_area_ha)}</dd></div>
            <div>
              <dt>Variance</dt>
              <dd>{liveEditing.area_variance_pct === null || liveEditing.area_variance_pct === undefined
                ? '--' : `${formatNumber(liveEditing.area_variance_pct, 1)}%`}</dd>
            </div>
          </dl>
          {geometry.isLoading
            ? <p className="state-note">Loading the boundary...</p>
            : (
              <PlotMap
                assets={assets.shapes}
                onChange={(polygon) => void saveBoundary(polygon)}
                placeHint={placeLine(liveEditing)}
                plotCode={liveEditing.plot_code}
                readOnly={!mayMap}
                value={currentPolygon}
              />
            )}
          <BoundaryStatus plotCode={liveEditing.plot_code} plotId={liveEditing.id} />
          {/* Marked and changed on Risk assessment (owner, 2026-09-27: "keep the risk assessment and
              plot page as seperate"); shown on this map for context only. */}
          <p className="muted" style={{ margin: 0 }}>Assets and risks show on the map; they are marked and changed on Risk assessment (Onboarding).</p>
        </>)}
        </>)}
      </RecordDrawer>

      <RecordDrawer
        footer={
          <>
            <button onClick={() => setMapping(null)} type="button">Close</button>
            {mayEditPlot && openPlot && (
              <button className="primary" onClick={() => { const row = openPlot; setMapping(null); openEdit(row); }} type="button">
                Edit details
              </button>
            )}
          </>
        }
        extraTab={openPlot ? {
          label: 'Satellite', icon: <Satellite size={14} />, content: <PlotMonitoring plotId={openPlot.id} />,
        } : undefined}
        // The plot's consents and their history, read without opening the edit form (owner,
        // 2026-09-27: "in plot, we need to have option to send consent and consent history").
        moreTabs={openPlot ? [
          { label: 'Consents', icon: <BadgeCheck size={14} />, content: (<>
            <PlotChecklist farmerCode={openPlot.farmer_code} linkOnly onPlotPage plotCode={openPlot.plot_code} plotId={openPlot.id} />
            <PlotConsentList linkOnly plotId={openPlot.id} />
          </>) },
          ...(openRights ? [{ label: openRights === 'family' ? 'Family members' : 'Sharecropping', icon: <Users size={14} />,
                              content: <RightsHolders mode={openRights} plotId={openPlot.id} /> }] : []),
          // Who may act for this plot besides its owner (owner, 2026-09-28).
          { label: 'Representatives', icon: <Users size={14} />, content: <PlotRepresentatives farmerId={openPlot.farmer_id} plotId={openPlot.id} /> },
        ] : undefined}
        onClose={() => setMapping(null)}
        open={Boolean(mapping)}
        record={openPlot ? { table: 'ops.farm_plots', id: openPlot.id, historyIds: [openPlot.current_geometry_id], invalidate: ['farm_plots'] } : null}
        subtitle={openPlotLabel}
        title={openPlot ? `Plot ${openPlot.plot_no ?? ''}${openPlot.plot_name ? ` - ${openPlot.plot_name}` : ''}`.trim() : 'Plot'}
        wide
      >
        {openPlot && (
          <>
            <div className="plot-owner">
              <FarmerPhoto name={openPlot.farmer_name} size={56}
                           url={openPlot.farmer_photo_document_id ? photos.data?.[openPlot.farmer_photo_document_id] : null} />
              <div className="plot-owner-text">
              <div>
                <strong>{openPlot.farmer_name ?? 'Unknown farmer'}</strong>
                {openPlot.farmer_code && <span className="muted"> · {openPlot.farmer_code}</span>}
              </div>
              <div className="muted">{placeLine(openPlot)}</div>
              <div className="plot-owner-count">
                Plot <strong>{openPlot.plot_no ?? '--'}</strong> of {openPlot.farmer_plot_count ?? '--'} held by this farmer
              </div>
              </div>
            </div>
            {/* Everything the form captures, read back under the form's own labels (field audit,
                owner 2026-09-27: "audit of views: shall be consistent with form"). */}
            <dl className="detail-grid">
              <div><dt>Status</dt><dd><StatusPill value={openPlot.is_draft ? 'draft' : openPlot.plot_status} /></dd></div>
              <div><dt>Plot name</dt><dd>{openPlot.plot_name ?? '--'}</dd></div>
              <div><dt>Land tenure</dt><dd>{(tenureTypes.data ?? []).find((row) => row.id === openPlot.land_tenure_type_id)?.label ?? '--'}</dd></div>
              {(openPlot.lease_from || openPlot.lease_to || openPlot.lease_agreement_no) && (
                <div><dt>Lease</dt><dd>{[
                  openPlot.lease_from || openPlot.lease_to ? `${openPlot.lease_from ? formatDate(openPlot.lease_from) : '?'} to ${openPlot.lease_to ? formatDate(openPlot.lease_to) : '?'}` : null,
                  openPlot.lease_agreement_no ? `agreement ${openPlot.lease_agreement_no}` : null,
                ].filter(Boolean).join(' · ')}</dd></div>
              )}
              <div><dt>Declared area (as per land record)</dt><dd>{openPlot.declared_area != null
                ? `${formatNumber(openPlot.declared_area, 3)} ${(units.data ?? []).find((unit) => unit.id === openPlot.declared_area_unit_id)?.label ?? ''} = ${formatArea(openPlot.declared_area_ha)}`
                : '--'}</dd></div>
              <div><dt>Mapped area</dt><dd>{formatArea(openPlot.mapped_area_ha)}</dd></div>
              <div>
                <dt>Variance</dt>
                <dd>{openPlot.area_variance_pct === null || openPlot.area_variance_pct === undefined
                  ? '--' : `${formatNumber(openPlot.area_variance_pct, 1)}%`}</dd>
              </div>
              <div><dt>Trees</dt><dd>{formatNumber(openPlot.total_tree_count ?? 0)}</dd></div>
              <div><dt>Risk</dt><dd><StatusPill value={openPlot.risk_status} /></dd></div>
            </dl>
            {geometry.isLoading
              ? <p className="state-note">Loading the boundary...</p>
              : (
                <PlotMap
                  assets={assets.shapes}
                  onChange={(polygon) => void saveBoundary(polygon)}
                  placeHint={placeLine(openPlot)}
                  plotCode={openPlot.plot_code}
                  readOnly={!mayMap}
                  value={currentPolygon}
                />
              )}
            <BoundaryStatus plotCode={openPlot.plot_code} plotId={openPlot.id} />
            <p className="muted" style={{ margin: 0 }}>Assets and risks show on the map; they are marked and changed on Risk assessment (Onboarding).</p>
            <dl className="detail-grid">
              <div><dt>Farm plot type</dt><dd>{optionLabel(PLOT_TYPES, openPlot.plot_type ?? 'production')}</dd></div>
              {(openPlot.hcv_categories ?? []).length > 0 && (
                <div><dt>High Conservation Values present</dt><dd>{(openPlot.hcv_categories ?? []).map((id) => optionLabel(HCV_CATEGORIES, id)).join(', ')}</dd></div>
              )}
              {(openPlot.conservation_aspects ?? []).length > 0 && (
                <div><dt>Conservation aspects</dt><dd>{(openPlot.conservation_aspects ?? []).map((id) => optionLabel(CONSERVATION_ASPECTS, id)).join(', ')}</dd></div>
              )}
              {openPlot.conservation_area_ha != null && <div><dt>Conserved area (ha)</dt><dd>{formatNumber(openPlot.conservation_area_ha, 2)}</dd></div>}
              {openPlot.conservation_notes && <div><dt>Conservation notes</dt><dd>{openPlot.conservation_notes}</dd></div>}
              <div><dt>Customary rights on this plot?</dt><dd>{openPlot.has_customary_rights == null ? 'Not assessed' : openPlot.has_customary_rights ? 'Yes' : 'No'}</dd></div>
              {openPlot.has_customary_rights && openPlot.customary_rights_detail && (
                <div><dt>Customary rights - description</dt><dd>{openPlot.customary_rights_detail}</dd></div>
              )}
              <div><dt>Indigenous neighbour</dt><dd>{openPlot.has_indigenous_neighbour ? 'Yes' : 'None recorded'}</dd></div>
            </dl>

            <div className="section-heading"><h3>Scope</h3></div>
            {(() => {
              const rows = viewScopes.data ?? [];
              const fscFm = rows.some((row) => row.scope_code === 'FSC_FM') || (openPlot.fsc_claim && openPlot.fsc_claim !== 'none') || openPlot.cb_approved_on;
              const others = PLOT_SCOPES.filter((scope) => !scope.usesPlotFields && rows.some((row) => row.scope_code === scope.code));
              if (!fscFm && others.length === 0) return <p className="muted" style={{ margin: 0 }}>In no scope yet. Choose Edit details, then the Scope tab.</p>;
              return (
                <dl className="detail-grid">
                  {fscFm && (
                    <div className="spec-view-wide"><dt>FSC FM (forest management)</dt><dd>{[
                      `FSC claim: ${optionLabel(FSC_CLAIMS, openPlot.fsc_claim ?? 'none')}`,
                      `approved by CB as member: ${openPlot.cb_approved_on ? formatDate(openPlot.cb_approved_on) : 'not yet'}`,
                      openPlot.cb_approval_ref ? `CB reference: ${openPlot.cb_approval_ref}` : null,
                      excisionText(rows.find((entry) => entry.scope_code === 'FSC_FM')),
                    ].filter(Boolean).join(' · ')}</dd></div>
                  )}
                  {others.map((scope) => {
                    const row = rows.find((entry) => entry.scope_code === scope.code)!;
                    const parts = scope.fields.map((field) => {
                      const value = row[field.column];
                      if (!value) return null;
                      const shown = field.kind === 'date' ? formatDate(String(value))
                        : field.kind === 'choice' ? field.choices?.find((choice) => choice.id === value)?.label ?? String(value) : String(value);
                      return `${field.label}: ${shown}`;
                    }).filter(Boolean);
                    return <div className="spec-view-wide" key={scope.code}><dt>{scope.label}</dt><dd>{parts.join(' · ') || 'In scope; no details yet.'}</dd></div>;
                  })}
                </dl>
              );
            })()}
            {/*
              * Neighbours and the six practice registers used to sit here (owner, 2026-09-25:
              * "we can remove these here" and "can have stakeholders also seperate page, and
              * remove from plot").
              *
              * Both are pages now: Stakeholders under Parties & consent, where a neighbour is a
              * stakeholder whose relation is adjoining land, and the six under Plot management.
              * The plot record keeps what is ABOUT THE PLOT ITSELF - its boundary, its tenure,
              * its land documents - and no longer carries eight collapsed sections of records
              * that are really about work, people and observations.
              */}
            <PlotLandDocuments farmerId={openPlot.farmer_id} plotId={openPlot.id} />
          </>
        )}
      </RecordDrawer>
      <PlotScopeBulk onClose={() => setBulkScope(false)} onDone={() => { setBulkScope(false); setSelectedPlots([]); setGridKey((key) => key + 1); }}
                     open={bulkScope} plots={(plots.data ?? []).filter((row) => selectedPlots.includes(row.id))} />
    </LiveScreen>
  );
}

/** Hectares on top, square metres beneath: the reporting unit and the field unit together. */
function AreaCell({ ha }: { ha?: number | string | null }) {
  if (ha === null || ha === undefined || ha === '') return <span className="muted">--</span>;
  return (
    <span className="area-cell">
      <span>{formatNumber(ha, 3)} ha</span>
      <small>{formatSqm(ha)}</small>
    </span>
  );
}

/** Tick any of a fixed list: HCV categories, conservation aspects. */
function ChipPicker({ options, value, onChange }: { options: Array<{ id: string; label: string }>; value: string[]; onChange: (value: string[]) => void }) {
  return (
    <div className="chip-picker">
      {options.map((option) => (
        <label className={`chip-option${value.includes(option.id) ? ' is-on' : ''}`} key={option.id}>
          <input checked={value.includes(option.id)} type="checkbox"
                 onChange={(event) => onChange(event.target.checked ? [...value, option.id] : value.filter((id) => id !== option.id))} />
          {option.label}
        </label>
      ))}
    </div>
  );
}

/** The plot's representatives: the farmer's, narrowed to those acting for this plot. */
function PlotRepresentatives({ farmerId, plotId }: { farmerId: string; plotId: string }) {
  const farmer = useRows<FarmerRow>('farmers', { filters: { id: farmerId } });
  const row = farmer.data?.[0];
  return <QueryState empty="The plot's farmer could not be read." query={farmer}>{row ? <RepresentativesTab farmer={row} plotId={plotId} /> : null}</QueryState>;
}
